import {
  Archive,
  Box,
  Cloud,
  CloudUpload,
  Download,
  FolderTree,
  Globe,
  KeyRound,
  Pencil,
  RefreshCw,
  Server,
  Trash2,
} from 'lucide-react';
import { BackfillOffer } from './BackfillOffer';
import { DownloadExported } from './DownloadExported';
import { SendAgainOffer } from './SendAgainOffer';
import { isAddable } from './destination-kinds';
import { stateLine } from './destination-state-line';
import type { PaigeDestination } from './paige.global';

export type DestinationAction = 'edit' | 'test' | 'retry' | 'reset-host-key' | 'remove';

export interface DestinationListProps {
  rows: PaigeDestination[];
  onOpen: (row: PaigeDestination) => void;
  onAct: (action: DestinationAction, row: PaigeDestination) => void;
}

/** The destinations of the "Send results to" card, one row each. */
export function DestinationList({ rows, onOpen, onAct }: DestinationListProps) {
  return (
    <ul className="paige-destinations">
      {rows.map((row) => (
        <DestinationRow key={row.id} row={row} onOpen={onOpen} onAct={onAct} />
      ))}
    </ul>
  );
}

interface RowProps {
  row: PaigeDestination;
  onOpen: (row: PaigeDestination) => void;
  onAct: (action: DestinationAction, row: PaigeDestination) => void;
}

/**
 * One destination: its kind, name, state line, and Edit, Test, Retry and Remove. An SFTP destination also shows
 * the server's host key once a test or delivery has trusted it, with "Reset trusted host key" for
 * when the server is rebuilt.
 */
function DestinationRow({ row, onOpen, onAct }: RowProps) {
  const editable = isAddable(row.kind);
  const hostKey = row.kind === 'sftp' ? row.settings.hostKeyFingerprint : undefined;
  return (
    <li className={`paige-destination is-${row.health}`}>
      <DestinationIcon kind={row.kind} size={16} />
      <span className="paige-destination-text">
        {editable ? (
          <button type="button" className="paige-destination-name" onClick={() => onOpen(row)}>
            {row.label}
          </button>
        ) : (
          <span className="paige-destination-name">{row.label}</span>
        )}
        <small>{stateLine(row)}</small>
        {hostKey && (
          <small
            className="paige-destination-host-key"
            title="The server's host key, trusted on the first connection"
          >
            <KeyRound size={11} aria-hidden="true" />
            Host key {hostKey}
          </small>
        )}
        {editable && <BackfillOffer row={row} />}
        {editable && <SendAgainOffer row={row} />}
      </span>
      {editable && <RowActions row={row} hostKey={hostKey} onAct={onAct} />}
      {row.kind === 'download' && <DownloadExported />}
    </li>
  );
}

/**
 * Edit and Test always; Reset trusted host key once one is pinned; Retry when deliveries wait or
 * fail; Remove always, last, as CapturePoint's connections list has Check, Replace key and Remove
 * (a quiet danger button that asks first).
 */
function RowActions({
  row,
  hostKey,
  onAct,
}: {
  row: PaigeDestination;
  hostKey: string | undefined;
  onAct: RowProps['onAct'];
}) {
  return (
    <span className="paige-destination-actions">
      <EditButton onClick={() => onAct('edit', row)} />
      <button type="button" className="quiet-button small" onClick={() => onAct('test', row)}>
        Test
      </button>
      {hostKey && (
        <button
          type="button"
          className="quiet-button small"
          onClick={() => onAct('reset-host-key', row)}
        >
          Reset trusted host key
        </button>
      )}
      {row.retrying > 0 || row.failed > 0 || row.health === 'down' ? (
        <button type="button" className="quiet-button small" onClick={() => onAct('retry', row)}>
          <RefreshCw size={12} aria-hidden="true" />
          Retry
        </button>
      ) : null}
      <RemoveButton label={row.label} onClick={() => onAct('remove', row)} />
    </span>
  );
}

function EditButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="quiet-button small" onClick={onClick}>
      <Pencil size={12} aria-hidden="true" />
      Edit
    </button>
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className="quiet-button small danger"
      aria-label={`Remove ${label}`}
      onClick={onClick}
    >
      <Trash2 size={12} aria-hidden="true" />
      Remove
    </button>
  );
}

/** The icon of a destination kind, in the row and in the sheet's head. */
export function DestinationIcon({ kind, size }: { kind: PaigeDestination['kind']; size: number }) {
  switch (kind) {
    case 'webhook':
      return <Globe size={size} aria-hidden="true" />;
    case 'sftp':
      return <Server size={size} aria-hidden="true" />;
    case 'content-central':
      return <Archive size={size} aria-hidden="true" />;
    case 'nucleus-one':
      return <FolderTree size={size} aria-hidden="true" />;
    case 'microsoft-365':
    case 'google-drive':
      return <Cloud size={size} aria-hidden="true" />;
    case 'dropbox':
      return <CloudUpload size={size} aria-hidden="true" />;
    case 'box':
      return <Box size={size} aria-hidden="true" />;
    default:
      return <Download size={size} aria-hidden="true" />;
  }
}
